// src/pages/LectureSupportContact.js
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import PageContainer from "../components/Layout/PageContainer";
import SeoHead from "../components/SeoHead";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { createSupportTicket } from "../api/sttApi";   // NEW API call
import LectureFaqMenu from "../components/LectureFaqMenu";
import "../styles/lectureUpload.css";

function detectUiLocale(navUploadLabel) {
  if (!navUploadLabel) return "en";
  if (navUploadLabel.includes("上传")) return "zh-Hans";
  if (/[\u4e00-\u9fff]/.test(navUploadLabel)) return "zh-Hant";
  return "en";
}

const LectureSupportContact = () => {
  const { t } = useLanguage();
  const { user } = useAuth();

  const navUploadLabel = t ? t("navUpload") : "Upload";
  const uiLocale = detectUiLocale(navUploadLabel);

  const strings = useMemo(() => {
    return {
      en: {
        headTitle: "Support / Contact | STT Lecture Service",
        headDesc:
          "Contact the STT team for upload issues, errors, or general enquiries.",
        pageTitle: "Support / Contact",
        intro:
          "Submit a support request. A support ticket will be created automatically.",
        lastUpdated: "Last updated: 02 Dec 2025 (Beta)",
        loginWarn: "Please sign in before submitting a support request.",
        note: "Do NOT include passwords, API keys, or confidential information.",

        faqMenuTitle: "STT FAQ Menu",
        faqMenuHowToUse: "How to Use",
        faqMenuTips: "Recording & Upload Tips",
        faqMenuLanguage: "Supported Languages",
        faqMenuPrivacy: "Privacy & Data",
        faqMenuVersion: "Version & Updates",
        faqMenuSupport: "Support / Contact",
        faqMenuAll: "View all STT FAQs",

        topicLabel: "Topic",
        topics: [
          "Bug Report",
          "Upload / Status Issue",
          "Account / Sign-in",
          "Other",
        ],

        emailLabel: "Signed-in Email",
        messageLabel: "Message",
        messagePH:
          "Describe the issue (steps, error message, expected behaviour)…",
        pageUrl: "Page URL",
        screenshot: "Screenshot URL (optional)",
        userAgent: "Browser / OS (auto)",

        submit: "Submit",
        submitting: "Submitting…",
        submitOk: (id) =>
          `Submitted! Ticket ID: ${id}. We will reply soon.`,
        submitErr: "Submit failed: ",

        emailAlt: "Or email us directly:",
      },

      "zh-Hant": {
        headTitle: "支援／聯絡我們｜STT 語音轉筆記",
        headDesc: "上傳問題、錯誤、帳號問題與一般查詢。",
        pageTitle: "支援／聯絡我們",
        intro: "提交支援請求，我們會自動建立案件。",

                lastUpdated: '最後更新：2025 年 12 月 2 日（Beta）',

loginWarn: "提交支援請求前需要先登入。",
        note: "請勿提供密碼、API 金鑰或高度機密資料。",

        faqMenuTitle: "STT 常見問題選單",
        faqMenuHowToUse: "如何使用",
        faqMenuTips: "錄音與上傳小貼士",
        faqMenuLanguage: "支援語言",
        faqMenuPrivacy: "隱私與資料安全",
        faqMenuVersion: "版本與更新記錄",
        faqMenuSupport: "支援／聯絡我們",
        faqMenuAll: "查看全部 STT 常見問題",

        topicLabel: "主題",
        topics: ["錯誤回報", "上傳／狀態問題", "帳號／登入問題", "其他"],

        emailLabel: "登入 Email",
        messageLabel: "問題內容",
        messagePH: "請描述問題（步驟、錯誤訊息、預期…）",
        pageUrl: "頁面 URL",
        screenshot: "截圖連結（選填）",
        userAgent: "瀏覽器／系統（自動）",

        submit: "送出",
        submitting: "送出中…",
        submitOk: (id) => `已送出！案件編號：${id}`,
        submitErr: "送出失敗：",

        emailAlt: "或直接來信：",
      },

      "zh-Hans": {
        headTitle: "客服／联系我们｜STT 语音转笔记",
        headDesc: "上传问题、报错、账号问题与一般咨询。",
        pageTitle: "客服／联系我们",
        intro: "提交客服请求，我们会自动创建工单。",

                lastUpdated: '最后更新：2025 年 12 月 2 日（Beta）',

loginWarn: "提交客服请求前需要先登录。",
        note: "请勿提供密码、API Key 或高度敏感信息。",

        faqMenuTitle: "STT 常见问题菜单",
        faqMenuHowToUse: "如何使用",
        faqMenuTips: "录音与上传小贴士",
        faqMenuLanguage: "支持语言",
        faqMenuPrivacy: "隐私与数据安全",
        faqMenuVersion: "版本与更新记录",
        faqMenuSupport: "客服／联系我们",
        faqMenuAll: "查看全部 STT 常见问题",

        topicLabel: "主题",
        topics: ["错误回报", "上传／状态问题", "账号／登录问题", "其他"],

        emailLabel: "登录 Email",
        messageLabel: "问题内容",
        messagePH: "描述问题（步骤、错误提示、预期…）",
        pageUrl: "页面 URL",
        screenshot: "截图链接（选填）",
        userAgent: "浏览器／系统（自动）",

        submit: "提交",
        submitting: "提交中…",
        submitOk: (id) => `已提交！工单编号：${id}`,
        submitErr: "提交失败：",

        emailAlt: "或直接写信：",
      },
    }[uiLocale];
  },  [uiLocale]);


  // FORM STATE
  const [topic, setTopic] = useState(strings.topics[0]);
  const [message, setMessage] = useState("");
  const [pageUrl, setPageUrl] = useState(
    typeof window !== "undefined" ? window.location.href : ""
  );
  const [screenshotUrl, setScreenshotUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent : "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setOkMsg("");
    setErrMsg("");

    try {
      if (!user) throw new Error(strings.loginWarn);
      if (!message || message.trim().length < 10)
        throw new Error("Message too short.");

      const payload = {
        topic,
        message,
        pageUrl,
        screenshotUrl,
        userAgent,
        email: user.email,
        service: "stt", // so backend can classify
      };

      const res = await createSupportTicket(payload);

      setOkMsg(strings.submitOk(res.id || ""));
      setMessage("");
      setScreenshotUrl("");
    } catch (err) {
      setErrMsg(strings.submitErr + (err.message || ""));
    } finally {
      setBusy(false);
    }
  };

  const url = "https://stt.aittsservice.online/lecture/faq/support";

  return (
    <PageContainer>
      <SeoHead
        title={strings.headTitle}
        description={strings.headDesc}
        url={url}
      />

      {/* HERO — same layout as How-to-Use */}
      <div className="card upload-hero">
      <div className="card-heading">
        <h1 className="card-title">{strings.pageTitle}</h1>
      </div>
      <p>{strings.intro}</p>
      <p style={{ fontSize: 12, marginTop: 4 }}>{strings.lastUpdated}</p>
    </div>

      {/* MAIN CONTENT — same 2-column grid */}
      <div className="card upload-card upload-faq-card">
        <div className="upload-layout upload-layout--faq howto-grid">
          {/* LEFT MENU */}
          <LectureFaqMenu active="support" />


          {/* RIGHT FORM */}
          <main className="upload-main upload-main--faq howto-main">
            {!user && (
              <div className="stt-warning-box">{strings.loginWarn}</div>
            )}
            <p className="faq-note">{strings.note}</p>

            <form onSubmit={handleSubmit} className="faq-form">
              {/* Topic */}
              <div className="form-group">
                <label>{strings.topicLabel}</label>
                <select
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  disabled={!user || busy}
                >
                  {strings.topics.map((opt) => (
                    <option key={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div className="form-group">
                <label>{strings.emailLabel}</label>
                <input value={user?.email || ""} readOnly />
              </div>

              {/* Message */}
              <div className="form-group">
                <label>{strings.messageLabel}</label>
                <textarea
                  rows={6}
                  value={message}
                  required
                  disabled={!user || busy}
                  placeholder={strings.messagePH}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>

              {/* Page URL + Screenshot */}
              <div className="form-row-2">
                <div>
                  <label>{strings.pageUrl}</label>
                  <input
                    value={pageUrl}
                    disabled={!user || busy}
                    onChange={(e) => setPageUrl(e.target.value)}
                  />
                </div>

                <div>
                  <label>{strings.screenshot}</label>
                  <input
                    value={screenshotUrl}
                    disabled={!user || busy}
                    onChange={(e) => setScreenshotUrl(e.target.value)}
                    placeholder="https://…"
                  />
                </div>
              </div>

              {/* User Agent */}
              <div className="form-group">
                <label>{strings.userAgent}</label>
                <input value={userAgent} readOnly />
              </div>

              <button
                type="submit"
                disabled={!user || busy}
                className="primary-button"
              >
                {busy ? strings.submitting : strings.submit}
              </button>

              {okMsg && <p className="faq-success">{okMsg}</p>}
              {errMsg && <p className="faq-error">{errMsg}</p>}
            </form>

            <p className="faq-email-alt">
              {strings.emailAlt}{" "}
              <a href="mailto:itcow.tts.general.enquiry@gmail.com">
                itcow.tts.general.enquiry@gmail.com
              </a>
            </p>
          </main>
        </div>
      </div>
    </PageContainer>
  );
};

export default LectureSupportContact;
